// Posição de estoque: saldo de cada produto em uma data (quantidade, custo médio e valor),
// derivado das movimentações. Depois da abertura, é aqui que se vê o saldo inicial.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { AlertTriangle, FileSpreadsheet, Search } from "lucide-react";
import { getEstoquePosicao } from "../../api";
import EstoqueLayout from "./EstoqueLayout";
import { AvisoErro, Campo, btnStyle, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

function Kpi({ C, rotulo, valor, destaque }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{rotulo}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: destaque || C.text }}>{valor}</div>
    </div>
  );
}

export default function PosicaoEstoqueView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [data, setData] = useState("");
  const [posicao, setPosicao] = useState(null);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [filtro, setFiltro] = useState("com_saldo"); // todos | com_saldo | abaixo_minimo

  const carregar = useCallback(async () => {
    setErro("");
    try {
      setPosicao(await getEstoquePosicao(data || null));
    } catch (e) {
      setErro(e.message);
    }
  }, [data]);

  useEffect(() => { carregar(); }, [carregar]);

  const categorias = useMemo(() => Array.from(new Set((posicao?.itens || []).map((i) => i.categoria_nome))), [posicao]);
  const itens = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (posicao?.itens || []).filter((i) =>
      (!termo || i.produto_nome.toLowerCase().includes(termo)) &&
      (!categoria || i.categoria_nome === categoria) &&
      (filtro === "todos" || (filtro === "com_saldo" && Number(i.quantidade) !== 0) || (filtro === "abaixo_minimo" && i.abaixo_minimo))
    );
  }, [posicao, busca, categoria, filtro]);
  const totalFiltrado = itens.reduce((s, i) => s + Number(i.valor), 0);

  function exportar() {
    const linhas = itens.map((i) => ({
      Categoria: i.categoria_nome,
      Produto: i.produto_nome,
      Unidade: i.unidade_sigla,
      Quantidade: Number(i.quantidade),
      "Custo médio (R$)": i.custo_medio === null ? "" : Number(i.custo_medio),
      "Valor (R$)": Number(i.valor),
      "Estoque mínimo": i.estoque_minimo ?? "",
      "Estoque máximo": i.estoque_maximo ?? "",
      "Abaixo do mínimo": i.abaixo_minimo ? "Sim" : "",
    }));
    const ws = XLSX.utils.json_to_sheet(linhas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Posição");
    XLSX.writeFile(wb, `posicao_estoque_${data || "atual"}.xlsx`);
  }

  const th = (align = "left") => ({ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.card });
  const td = (align = "left") => ({ padding: "9px 12px", fontSize: 13, textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: align === "right" ? "nowrap" : "normal" });

  return (
    <EstoqueLayout titulo="Posição de Estoque" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {posicao && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
            <Kpi C={C} rotulo="Valor total em estoque" valor={formatBRL(posicao.valor_total)} />
            <Kpi C={C} rotulo="Produtos com saldo" valor={posicao.produtos_com_saldo} />
            <Kpi C={C} rotulo="Abaixo do mínimo" valor={posicao.produtos_abaixo_minimo} destaque={posicao.produtos_abaixo_minimo ? C.danger : undefined} />
          </div>

          {posicao.itens.every((i) => Number(i.quantidade) === 0) && (
            <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 13, color: C.muted }}>
              Ainda não há saldo. O estoque começa pela <button onClick={() => navigate("/estoque/vistoria")} style={{ background: "none", border: "none", color: C.brand, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13 }}>vistoria de abertura</button>.
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "flex-end" }}>
            <div style={{ flex: "0 1 170px" }}>
              <Campo label="Posição em" C={C}>
                <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={inputStyle(C)} />
              </Campo>
            </div>
            <div style={{ position: "relative", flex: "1 1 200px" }}>
              <Search size={15} style={{ position: "absolute", left: 12, bottom: 13, color: C.muted }} />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto" style={{ ...inputStyle(C), paddingLeft: 34 }} />
            </div>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 170px", width: "auto" }}>
              <option value="">Todas as categorias</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 170px", width: "auto" }}>
              <option value="com_saldo">Com saldo</option>
              <option value="abaixo_minimo">Abaixo do mínimo</option>
              <option value="todos">Todos</option>
            </select>
            <button onClick={exportar} disabled={!itens.length} style={{ ...btnStyle(C.success), opacity: itens.length ? 1 : 0.5 }}>
              <FileSpreadsheet size={15} /> Excel
            </button>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            {itens.length} produto(s) · {formatBRL(totalFiltrado)}{data ? ` · saldo em ${data.split("-").reverse().join("/")}` : " · saldo atual"}
          </div>

          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {itens.map((i) => (
                <div key={i.produto_id} style={{ background: C.card, border: `1px solid ${i.abaixo_minimo ? C.danger : C.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{i.produto_nome}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{i.categoria_nome}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{formatQtd(i.quantidade)} {i.unidade_sigla}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{formatBRL(i.valor)}</div>
                    </div>
                  </div>
                  {i.abaixo_minimo && <div style={{ fontSize: 12, color: C.danger, marginTop: 6, display: "flex", gap: 4, alignItems: "center" }}><AlertTriangle size={13} /> Abaixo do mínimo ({formatQtd(i.estoque_minimo)})</div>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "65vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th()}>Produto</th>
                    <th style={th()}>Categoria</th>
                    <th style={th("right")}>Quantidade</th>
                    <th style={th("right")}>Custo médio</th>
                    <th style={th("right")}>Valor</th>
                    <th style={th("right")}>Mín. / Máx.</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.length === 0 && <tr><td colSpan={6} style={{ ...td("center"), color: C.muted, padding: 24 }}>Nenhum produto.</td></tr>}
                  {itens.map((i) => (
                    <tr key={i.produto_id}>
                      <td style={{ ...td(), fontWeight: 600 }}>
                        {i.produto_nome}
                        {!i.produto_ativo && <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}> (inativo)</span>}
                      </td>
                      <td style={{ ...td(), color: C.muted }}>{i.categoria_nome}</td>
                      <td style={{ ...td("right"), color: i.abaixo_minimo ? C.danger : C.text, fontWeight: i.abaixo_minimo ? 700 : 400 }}>
                        {i.abaixo_minimo && <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />}
                        {formatQtd(i.quantidade)} {i.unidade_sigla}
                      </td>
                      <td style={td("right")}>{formatBRL(i.custo_medio)}</td>
                      <td style={{ ...td("right"), fontWeight: 700 }}>{formatBRL(i.valor)}</td>
                      <td style={{ ...td("right"), color: C.muted }}>{formatQtd(i.estoque_minimo)} / {formatQtd(i.estoque_maximo)}</td>
                    </tr>
                  ))}
                </tbody>
                {itens.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ ...td(), fontWeight: 800 }}>Total</td>
                      <td style={{ ...td("right"), fontWeight: 800 }}>{formatBRL(totalFiltrado)}</td>
                      <td style={td()} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </EstoqueLayout>
  );
}
